import React, { useState } from 'react'
import useSWR, { useSWRConfig } from 'swr'
import Client from '../../models/client'
import { ActionIcon, Box, Button, Center, Group, Loader, Table, Text, ThemeIcon, Title, Tooltip } from '@mantine/core'
import { GearIcon, PencilSimpleIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react'
import { notifications } from '@mantine/notifications'
import { useDisclosure } from '@mantine/hooks'
import MailConfForm from '../components/MailConfForm'
import { ReportMailCreateModal } from '../components/ReportMailCreateModal'
import { ReportMailUpdateModal } from '../components/ReportMailUpdateModal'
import { FETCH_KEYS, deleteReportMail, fetcherWithAuth } from '../api'

interface Props {
  client: Client
}

type ReportMailRow = {
  id: number
  name: string
  send_to: string
  client_id: number
}

const ReportMailsContainer: React.FC<Props> = ({ client }) => {
  const { mutate: mutateGlobal } = useSWRConfig()
  const { data, mutate: mutateReportMails } = useSWR(FETCH_KEYS.reportMails(client.id), fetcherWithAuth)

  const [openedCreate, createModal] = useDisclosure(false)
  const [openedUpdate, updateModal] = useDisclosure(false)
  const [editingReportMail, setEditingReportMail] = useState<ReportMailRow | null>(null)

  if (!data) {
    return (
      <Center mih={200}>
        <Loader />
      </Center>
    )
  }

  const reportMails: ReportMailRow[] = data.report_mails

  const handleClickEdit = (row: ReportMailRow) => {
    setEditingReportMail(row)
    updateModal.open()
  }

  const handleClickDelete = async (row: ReportMailRow) => {
    const confirmMessage = `削除しますか？\n\n名前：${row.name ?? ''}\nメールアドレス：${row.send_to ?? ''}\n※一度削除すると元に戻せません。`
    if (!window.confirm(confirmMessage)) return

    try {
      await deleteReportMail(client.id, row.id)
      await mutateReportMails()
      notifications.show({ message: '通知先を削除しました', color: 'teal' })
    } catch (e) {
      notifications.show({ message: '通知先の削除に失敗しました', color: 'red' })
    }
  }

  const handleSavedMailConf = () => {
    mutateGlobal(FETCH_KEYS.fetchClient(client.id))
  }

  return (
    <Box>
      <Group gap={12} mb={24}>
        <ThemeIcon variant="transparent" color="#4BC1C7">
          <GearIcon weight="fill" size={36} />
        </ThemeIcon>
        <Title size={24} fw={600} c="#000000CC">
          CV通知
        </Title>
      </Group>

      <MailConfForm
        clientId={client.id}
        initialValues={{
          report_web_cv: client.report_web_cv,
          report_coming_cv: client.report_coming_cv,
          is_report_inviter_name: client.is_report_inviter_name,
          is_report_guest_name: client.is_report_guest_name,
          is_report_shop_name: client.is_report_shop_name,
          is_report_coming_mail: client.is_report_coming_mail,
          is_report_coming_tel: client.is_report_coming_tel,
          is_report_coming_address: client.is_report_coming_address,
          is_report_coming_other: client.is_report_coming_other,
          notify_guest_shop: client.notify_guest_shop,
          notify_inviter_shop: client.notify_inviter_shop,
        }}
        invyServiceUse={client.invy_service_use}
        onSaved={handleSavedMailConf}
      />

      <Group gap={12} align="center" mt={40} mb={16}>
        <Title order={2} size={18} fw={600}>
          通知先
        </Title>
      </Group>

      <Button
        variant="filled"
        radius="xl"
        leftSection={<PlusIcon size={16} weight="bold" />}
        onClick={createModal.open}
      >
        新規通知先
      </Button>

      <Box style={{ border: '1px solid #0000001A', borderRadius: 8, overflow: 'hidden', marginTop: 16 }}>
        <Table withColumnBorders styles={{ th: { backgroundColor: '#00000008' } }}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={80}>id</Table.Th>
              <Table.Th>名前</Table.Th>
              <Table.Th>メールアドレス</Table.Th>
              <Table.Th w={80} ta="center">編集</Table.Th>
              <Table.Th w={80} ta="center">削除</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {reportMails.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text c="#00000099" ta="center" py="md">
                    通知先が登録されていません
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              reportMails.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>{row.id}</Table.Td>
                  <Table.Td>{row.name}</Table.Td>
                  <Table.Td>{row.send_to}</Table.Td>
                  <Table.Td ta="center">
                    <Tooltip label="編集">
                      <ActionIcon variant="subtle" color="gray" onClick={() => handleClickEdit(row)}>
                        <PencilSimpleIcon size={18} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                  <Table.Td ta="center">
                    <Tooltip label="削除">
                      <ActionIcon variant="subtle" color="red" onClick={() => handleClickDelete(row)}>
                        <TrashIcon size={18} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Box>

      <ReportMailCreateModal
        opened={openedCreate}
        onClose={createModal.close}
        clientId={client.id}
        onSaved={mutateReportMails}
      />
      {editingReportMail && (
        <ReportMailUpdateModal
          opened={openedUpdate}
          onClose={updateModal.close}
          clientId={client.id}
          reportMail={editingReportMail}
          onSaved={mutateReportMails}
        />
      )}
    </Box>
  )
}

export default ReportMailsContainer
